import { BREAKPOINTS } from '@app/constants/breakpoints';
import { getIsAccountEditingEnabled, getIsBuyer, getIsLoggedIn, getIsSeller, getUser } from '@app/ducks';
import { getDeliveryWindowIdsFromVariants } from '@app/logic/deliveryWindows';
import { getHasPermissions } from '@app/logic/User';
import { compiledPaths } from '@app/paths';
import { useLookbookProductsByIds } from '@app/services/hooks/lookbook/useLookbookProductsByIds';
import { useAmplitude } from '@app/utils/analytics/useAmplitude';
import { cn } from '@app/utils/cn';
import { getIsFeatureEnabled } from '@app/utils/getIsFeatureEnabled';
import { useIsScreenSize } from '@app/utils/hooks';
import { useModalToggle } from '@app/utils/hooks/useModalToggle';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { RequestAccessModal } from '../account/RequestAccessModal';
import { CreateAccountModal } from '../accounts/CreateAccount/CreateAccountModal';
import { useLookbookContext } from '../lookbook/context/LookbookContext';
import { useLookbookWishlistContext } from '../lookbook/context/LookbookWishlistContext';
import { Button } from '../various/Button';
import { Account, ChevronLeft } from '../various/Icons';

import { LookbookHeaderTitle } from './LookbookHeaderTitle';

interface Props {
  name?: string;
  shareLookbookUrl: string;
  shouldHideActions?: boolean;
}
export const LookbookHeaderBanner = ({ name, shareLookbookUrl, shouldHideActions = false }: Props) => {
  const { t } = useTranslation(['common', 'accounts', 'products', 'lookbook']);
  const user = useSelector(getUser);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const hasUserCreateAccountPermissions = isLoggedIn && getHasPermissions(user, 'CREATE_ACCOUNT');
  const isAccountEditingEnabled = useSelector(getIsAccountEditingEnabled);
  const isBuyer = useSelector(getIsBuyer);
  const isSeller = useSelector(getIsSeller);
  const isMobile = useIsScreenSize(BREAKPOINTS.SM);
  const isTablet = useIsScreenSize(BREAKPOINTS.LG);
  const isDesktop = !isMobile && !isTablet;
  const lookbookName = isDefined(name) && !isEmpty(name) ? name : t('common:lookbook');
  const { showModal: showRequestAccountModal, isOpen: showRequestAccountModalOpen, hideModal: hideRequestAccountModal } = useModalToggle();
  const { showModal: showCreateAccountModal, isOpen: showCreateAccountModalOpen, hideModal: hideCreateAccountModal } = useModalToggle();
  const { trackEvent } = useAmplitude();
  const isLookbookWishlistEnabled = getIsFeatureEnabled('ENABLE_LOOKBOOK_WISHLIST');
  const { lookbookId, allowAccountRequest } = useLookbookContext();
  const { wishlist } = useLookbookWishlistContext();
  const { data: wishlistProductsData } = useLookbookProductsByIds({ lookbookId, products: [...wishlist] });
  const products = React.useMemo(() => wishlistProductsData ?? [], [wishlistProductsData]);
  const deliveryWindows = React.useMemo(() => getDeliveryWindowIdsFromVariants(products), [products]);

  const handleAccountCreated = () => {
    trackEvent({ name: 'lookbookPage.banner.createAccount.created' });
    hideCreateAccountModal();
  };

  const handleCreateAccountClick = () => {
    showCreateAccountModal();
    trackEvent({ name: 'lookbookPage.banner.callToAction.click', properties: { userType: 'seller' } });
  };

  const handleRequestAccountClick = () => {
    showRequestAccountModal();
    trackEvent({ name: 'lookbookPage.banner.callToAction.click', properties: { userType: 'viewer' } });
  };

  return (
    <>
      <div
        className={cn('m-1 flex h-10 items-center justify-between gap-4 rounded-small bg-white px-7', {
          'px-3': isTablet,
        })}
      >
        {isBuyer ?
          <>
            <div className="flex flex-1">
              <Button as={Link} to={compiledPaths.ROOT({})} variant="bordered" color="secondary" size="small" icon={ChevronLeft}>
                {t('common:my_showroom')}
              </Button>
            </div>
            <LookbookHeaderTitle lookbookName={lookbookName} shareLookbookUrl={shareLookbookUrl} className="lg:flex-2" />
            {isDesktop && <div className="flex-1" />}
          </>
        : isSeller ?
          <>
            {isDesktop && <div className="flex-1" />}
            <LookbookHeaderTitle lookbookName={lookbookName} shareLookbookUrl={shareLookbookUrl} className="lg:flex-2" />
            <div className="flex flex-1 justify-end">
              {!shouldHideActions && hasUserCreateAccountPermissions && isAccountEditingEnabled && (
                <Button onClick={handleCreateAccountClick} color="primary" size="medium" icon={!isMobile ? Account : undefined}>
                  {t('accounts:create_account')}
                </Button>
              )}
            </div>
            <CreateAccountModal
              isOpen={showCreateAccountModalOpen}
              onClose={hideCreateAccountModal}
              onSuccess={handleAccountCreated}
              products={[...wishlist]}
              deliveryWindows={[...deliveryWindows]}
              shouldCreateSelection
              createSource="lookbook"
            />
          </>
        : <>
            {isDesktop && <div className="flex-1" />}
            <LookbookHeaderTitle lookbookName={lookbookName} shareLookbookUrl={shareLookbookUrl} className="lg:flex-2" />
            <div className="flex flex-1 justify-end">
              {!shouldHideActions && isLookbookWishlistEnabled && allowAccountRequest && (
                <Button color="primary" size="medium" onClick={handleRequestAccountClick}>
                  {t('accounts:request_account')}
                </Button>
              )}
            </div>
          </>
        }
      </div>
      <RequestAccessModal
        lookbookId={lookbookId}
        wishlist={[...wishlist]}
        isOpen={showRequestAccountModalOpen}
        onSuccess={hideRequestAccountModal}
        onClose={hideRequestAccountModal}
      />
    </>
  );
};
